import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { loginMlistApi } from "../../api/mlistAuth";
import { getMlistApiConfig } from "../../api/mlistClient";
import AdminToolsMlistAuthStatus from "./AdminToolsMlistAuthStatus";

const Wrap = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 24px 16px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  margin-top: 12px;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #444;
  margin-bottom: 12px;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
`;

const Button = styled.button`
  padding: 9px 14px;
  border-radius: 8px;
  border: 1px solid #111;
  background: #111;
  color: #fff;
  cursor: pointer;
  width: 100%;
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #f1b7b7;
  background: #fff5f5;
  color: #7a1c1c;
  font-size: 13px;
`;

const WarnBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #f0e0a8;
  background: #fffbeb;
  color: #6b5700;
  font-size: 13px;
`;

const Muted = styled.p`
  margin: 0 0 12px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.45;
`;

const DevPanel = styled.pre`
  margin: 12px 0 0;
  padding: 10px;
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 11px;
  overflow: auto;
`;

const isDev = process.env.NODE_ENV === "development";

export default function AdminToolsLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.from || "/admin-tools";

  const apiConfig = useMemo(() => getMlistApiConfig(), []);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const canSubmit = apiConfig.valid && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!apiConfig.valid) return;
    setErr(null);
    setSubmitting(true);
    try {
      await loginMlistApi({ email, password });
      navigate(returnTo, { replace: true });
    } catch (error) {
      const message =
        error?.data?.detail ||
        error?.message ||
        "Login failed. Check email, password, and staff access.";
      setErr(String(message));
    } finally {
      setSubmitting(false);
    }
  }

  if (!apiConfig.configured) {
    return (
      <Wrap>
        <h1 style={{ margin: 0, fontSize: 22 }}>Admin Tools — MList API login</h1>
        <Card>
          <WarnBox>
            <strong>REACT_APP_MLIST_API_BASE_URL not configured</strong>
            <div style={{ marginTop: 6 }}>
              Set the env var in <code>frontend/.env</code> and restart the dev
              server.
            </div>
          </WarnBox>
        </Card>
        <div style={{ marginTop: 12 }}>
          <Link to="/admin-tools">← Admin Tools</Link>
        </div>
      </Wrap>
    );
  }

  if (!apiConfig.valid) {
    return (
      <Wrap>
        <h1 style={{ margin: 0, fontSize: 22 }}>Admin Tools — MList API login</h1>
        <Card>
          <WarnBox>
            <strong>Invalid MList API base URL</strong>
            <div style={{ marginTop: 6 }}>{apiConfig.errorMessage}</div>
            <div style={{ marginTop: 6 }}>
              Raw env value: <code>{apiConfig.rawInput}</code>
            </div>
          </WarnBox>
          {isDev ? (
            <DevPanel>
              {JSON.stringify(
                {
                  rawInput: apiConfig.rawInput,
                  normalizedBase: apiConfig.normalizedBase,
                  protocol: apiConfig.protocol,
                  tokenUrl: apiConfig.tokenUrl,
                },
                null,
                2
              )}
            </DevPanel>
          ) : null}
        </Card>
        <div style={{ marginTop: 12 }}>
          <Link to="/admin-tools">← Admin Tools</Link>
        </div>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <h1 style={{ margin: 0, fontSize: 22 }}>Admin Tools — MList API login</h1>
      <Muted>
        This login only enables MList API relationship bridge panels. Legacy
        hoodie API still requires the old Django session/cookie login — use the
        main site login or Django admin for that.
      </Muted>

      <AdminToolsMlistAuthStatus />

      <Card>
        <form onSubmit={handleSubmit}>
          <Label>
            Email (mlist-api staff account)
            <Input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={!canSubmit}
            />
          </Label>
          <Label>
            Password
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={!canSubmit}
            />
          </Label>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? "Signing in…" : "Sign in to MList API"}
          </Button>
        </form>

        {err ? <ErrorBox>{err}</ErrorBox> : null}

        <Muted style={{ marginTop: 14, marginBottom: 0 }}>
          Token endpoint: <code>{apiConfig.tokenUrl}</code>
          <br />
          Stored in <code>localStorage.accessToken</code>
          {", "}
          <code>localStorage.refreshToken</code> when returned.
        </Muted>

        {isDev ? (
          <DevPanel>
            {JSON.stringify(
              {
                rawEnv: apiConfig.rawInput,
                normalizedBase: apiConfig.normalizedBase,
                protocolDetected: Boolean(apiConfig.protocol),
                protocol: apiConfig.protocol,
                apiBaseUrl: apiConfig.apiBaseUrl,
                tokenUrl: apiConfig.tokenUrl,
              },
              null,
              2
            )}
          </DevPanel>
        ) : null}
      </Card>

      <div style={{ marginTop: 12, fontSize: 13 }}>
        <Link to={returnTo}>← Back</Link>
        <span style={{ color: "#999" }}> • </span>
        <Link to="/admin-tools">Admin Tools</Link>
      </div>
    </Wrap>
  );
}
